import { createApi } from '@reduxjs/toolkit/query/react';
import { AppUser } from '.';
import {
  AppRole,
  PowerBiReport,
  addActiveStatusToUserRoles,
  axiosBaseQuery,
} from '../../.';

export const appUserApi = createApi({
  reducerPath: 'appUserApi',
  baseQuery: axiosBaseQuery(),
  // Self-heal cached rejections from transient failures (cert untrust, brief
  // network hiccup, API restart). Without this, a single ERR_NETWORK sticks in
  // the cache as `rejected` and the entire SPA stays blank — getUser drives
  // mockUser, mockUser drives the menu, no menu = blank sidebar. Refetch on
  // every mount when the cached data is older than 30 seconds.
  refetchOnMountOrArgChange: 30,
  endpoints: builder => ({
    getUser: builder.query<AppUser, void>({
      query: () => ({
        url: 'users',
        method: 'GET',
      }),
    }),
    getAllUsers: builder.query<AppUser[], void>({
      query: () => ({
        url: 'users/all',
        method: 'GET',
      }),
    }),
    getAllUserRoles: builder.query<AppRole[], void>({
      query: () => ({
        url: 'users/roles',
        method: 'GET',
      }),
      transformResponse: (userRoles: AppRole[], meta, request) =>
        addActiveStatusToUserRoles(userRoles),
    }),
    getRolesForUser: builder.query<AppRole[], number | undefined | null>({
      query: uid => ({
        url: `users/roles/${uid}`,
        method: 'GET',
      }),
    }),
    updateRolesForUser: builder.mutation<
      void,
      { uid: number; roleIds: number[] }
    >({
      query: ({ uid, roleIds }) => {
        return {
          url: `users/roles/${uid}`,
          method: 'PUT',
          data: roleIds,
        };
      },
    }),
    updateClientRolesForUser: builder.mutation<
      void,
      { uid: number; roleIds: number[] }
    >({
      query: ({ uid, roleIds }) => {
        return {
          url: `users/clientRoles/${uid}`,
          method: 'PUT',
          data: roleIds,
        };
      },
    }),
    getUsersByRole: builder.query<
      AppUser[],
      { uid: number | undefined | null; rid: number | undefined | null }
    >({
      query: request => ({
        url: `users/${request.uid}/role/${request.rid}/all`,
        method: 'GET',
      }),
    }),
    postUserPowerBiForcedClaims: builder.mutation<
      void,
      { userId: number; powerBiReports: PowerBiReport[] }
    >({
      query: ({ userId, powerBiReports }) => {
        return {
          url: `users/${userId}/powerbi/forcedClaims`,
          method: 'POST',
          data: powerBiReports,
        };
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetAllUsersQuery,
  useGetAllUserRolesQuery,
  useGetRolesForUserQuery,
  useUpdateRolesForUserMutation,
  useUpdateClientRolesForUserMutation,
  useGetUsersByRoleQuery,
  usePostUserPowerBiForcedClaimsMutation,
  endpoints: {
    getUser: { useQueryState: useGetUserQueryState },
  },
} = appUserApi;
